// Reusable answer lists, each a value set over the answer codes.
import type { ValueSet } from '@medplum/fhirtypes'
import { ASSESSMENT_TEMPLATE_ANSWER_SETS } from '#src/terminology/assessmentTemplates.ts'
import { INSTRUMENT_ANSWER_SETS } from '#src/terminology/instruments/instrumentTerminology.ts'
import { PCP_ANSWER_SETS } from '#src/terminology/pcpInitialAssessment.ts'
import { RD_ANSWER_SETS } from '#src/terminology/rdInitialAssessment.ts'
import { ASSESSMENT_ANSWERS_SYSTEM } from '#src/terminology/types.ts'

export const ANSWER_SETS: ValueSet[] = [
  {
    resourceType: 'ValueSet',
    url: 'https://www.seenhealth.org/fhir/ValueSet/oral-med-management-answers',
    version: '1',
    name: 'OralMedManagementAnswers',
    title: 'Oral medication management',
    status: 'active',
    meta: {
      tag: [
        {
          system: 'https://www.seenhealth.org/fhir/managed-by',
          code: 'code',
        },
      ],
    },
    compose: {
      include: [
        {
          system: ASSESSMENT_ANSWERS_SYSTEM,
          concept: [
            {
              code: 'oral-med-independent',
              display:
                'Independently and consistently takes correct oral medications at proper dosages at the correct times without any assistance or supervision',
            },
            {
              code: 'oral-med-with-setup-or-reminders',
              display:
                'Takes oral medications at correct times if doses are prepared in advance, given daily reminders, or someone develops a drug diary or chart',
            },
            {
              code: 'oral-med-independent-sometimes',
              display:
                'Takes oral medications independently some (but not all) of the time',
            },
            {
              code: 'oral-med-administered-by-other',
              display:
                'Does not take oral medications unless administered by someone else',
            },
            {
              code: 'oral-med-none-prescribed',
              display: 'Not applicable — no orally prescribed medications',
            },
          ],
        },
      ],
    },
  },
  {
    resourceType: 'ValueSet',
    url: 'https://www.seenhealth.org/fhir/ValueSet/bladder-control-answers',
    version: '1',
    name: 'BladderControlAnswers',
    title: 'Bladder control (local stand-in for LL5782-9)',
    status: 'active',
    meta: {
      tag: [
        {
          system: 'https://www.seenhealth.org/fhir/managed-by',
          code: 'code',
        },
      ],
    },
    compose: {
      include: [
        {
          system: ASSESSMENT_ANSWERS_SYSTEM,
          concept: [
            {
              code: 'bladder-continent',
              display: 'Continent',
            },
            {
              code: 'bladder-occasional',
              display: 'Occasional incontinence',
            },
            {
              code: 'bladder-incontinent',
              display: 'Incontinent',
            },
          ],
        },
      ],
    },
  },
  {
    resourceType: 'ValueSet',
    url: 'https://www.seenhealth.org/fhir/ValueSet/yes-no',
    version: '1',
    name: 'YesNo',
    title: 'Yes or no',
    status: 'active',
    meta: {
      tag: [
        {
          system: 'https://www.seenhealth.org/fhir/managed-by',
          code: 'code',
        },
      ],
    },
    compose: {
      include: [
        {
          system: ASSESSMENT_ANSWERS_SYSTEM,
          concept: [
            {
              code: 'yes',
              display: 'Yes',
            },
            {
              code: 'no',
              display: 'No',
            },
          ],
        },
      ],
    },
  },
  {
    resourceType: 'ValueSet',
    url: 'https://www.seenhealth.org/fhir/ValueSet/interpreter-need',
    version: '1',
    name: 'InterpreterNeed',
    title: 'Interpreter need',
    status: 'active',
    meta: {
      tag: [
        {
          system: 'https://www.seenhealth.org/fhir/managed-by',
          code: 'code',
        },
      ],
    },
    compose: {
      include: [
        {
          system: ASSESSMENT_ANSWERS_SYSTEM,
          concept: [
            {
              code: 'none-needed',
              display: 'None needed',
            },
            {
              code: 'yes',
              display: 'Yes',
            },
          ],
        },
      ],
    },
  },
  {
    resourceType: 'ValueSet',
    url: 'https://www.seenhealth.org/fhir/ValueSet/interpreter-source',
    version: '1',
    name: 'InterpreterSource',
    title: 'Interpreter source',
    status: 'active',
    meta: {
      tag: [
        {
          system: 'https://www.seenhealth.org/fhir/managed-by',
          code: 'code',
        },
      ],
    },
    compose: {
      include: [
        {
          system: ASSESSMENT_ANSWERS_SYSTEM,
          concept: [
            {
              code: 'interpreter-organization',
              display: 'Vendor or service',
            },
            {
              code: 'interpreter-practitioner',
              display: 'Identified interpreter',
            },
            {
              code: 'interpreter-related-person',
              display: 'Family or caregiver',
            },
          ],
        },
      ],
    },
  },
  {
    resourceType: 'ValueSet',
    url: 'https://www.seenhealth.org/fhir/ValueSet/assessment-mode',
    version: '1',
    name: 'AssessmentMode',
    title: 'Assessment mode',
    status: 'active',
    meta: {
      tag: [
        {
          system: 'https://www.seenhealth.org/fhir/managed-by',
          code: 'code',
        },
      ],
    },
    compose: {
      include: [
        {
          system: ASSESSMENT_ANSWERS_SYSTEM,
          concept: [
            {
              code: 'in-person',
              display: 'In Person',
            },
            {
              code: 'phone-mode',
              display: 'Over the Phone',
            },
            {
              code: 'telehealth',
              display: 'Telehealth',
            },
          ],
        },
      ],
    },
  },
  {
    resourceType: 'ValueSet',
    url: 'https://www.seenhealth.org/fhir/ValueSet/assessment-location',
    version: '1',
    name: 'AssessmentLocation',
    title: 'Assessment location',
    status: 'active',
    meta: {
      tag: [
        {
          system: 'https://www.seenhealth.org/fhir/managed-by',
          code: 'code',
        },
      ],
    },
    compose: {
      include: [
        {
          system: ASSESSMENT_ANSWERS_SYSTEM,
          concept: [
            {
              code: 'rehab-gym',
              display: 'Rehab Gym',
            },
            {
              code: 'day-center',
              display: 'Day Center',
            },
            {
              code: 'ppt-home',
              display: 'PPT’s Home',
            },
            {
              code: 'clinic',
              display: 'Clinic',
            },
            {
              code: 'telehealth',
              display: 'Telehealth',
            },
          ],
        },
      ],
    },
  },
  {
    resourceType: 'ValueSet',
    url: 'https://www.seenhealth.org/fhir/ValueSet/interpreter-language',
    version: '1',
    name: 'InterpreterLanguage',
    title: 'Interpreter language',
    status: 'active',
    meta: {
      tag: [
        {
          system: 'https://www.seenhealth.org/fhir/managed-by',
          code: 'code',
        },
      ],
    },
    compose: {
      include: [
        {
          system: ASSESSMENT_ANSWERS_SYSTEM,
          concept: [
            {
              code: 'mandarin',
              display: 'Mandarin',
            },
            {
              code: 'cantonese',
              display: 'Cantonese',
            },
            {
              code: 'english',
              display: 'English',
            },
          ],
        },
      ],
    },
  },
  {
    resourceType: 'ValueSet',
    url: 'https://www.seenhealth.org/fhir/ValueSet/flooring-hazards',
    version: '1',
    name: 'FlooringHazards',
    title: 'Flooring and carpeting hazards',
    status: 'active',
    meta: {
      tag: [
        {
          system: 'https://www.seenhealth.org/fhir/managed-by',
          code: 'code',
        },
      ],
    },
    compose: {
      include: [
        {
          system: ASSESSMENT_ANSWERS_SYSTEM,
          concept: [
            {
              code: 'holes-in-floor',
              display: 'Holes in floor',
            },
            {
              code: 'wires-in-walkways',
              display: 'Electric wires in walkways',
            },
            {
              code: 'loose-rugs-cords',
              display: 'Loose rugs, cords, or tubes',
            },
            {
              code: 'other-specify',
              display: 'Other, please specify:',
            },
          ],
        },
      ],
    },
  },
  {
    resourceType: 'ValueSet',
    url: 'https://www.seenhealth.org/fhir/ValueSet/bathroom-hazards',
    version: '1',
    name: 'BathroomHazards',
    title: 'Bathroom and toilet hazards',
    status: 'active',
    meta: {
      tag: [
        {
          system: 'https://www.seenhealth.org/fhir/managed-by',
          code: 'code',
        },
      ],
    },
    compose: {
      include: [
        {
          system: ASSESSMENT_ANSWERS_SYSTEM,
          concept: [
            {
              code: 'toilet-faucets',
              display: 'Non-operating toilet or faucets',
            },
            {
              code: 'leaking-pipes',
              display: 'Leaking pipes',
            },
            {
              code: 'no-rails',
              display: 'No rails, though needed',
            },
            {
              code: 'slippery-bathtub',
              display: 'Slippery bathtub',
            },
            {
              code: 'outside-toilet',
              display: 'Outside toilet',
            },
            {
              code: 'bath-distance',
              display: 'Distance from bedroom to bathroom far',
            },
            {
              code: 'lack-of-warm-water',
              display: 'Lack of warm water',
            },
            {
              code: 'other-specify',
              display: 'Other, please specify:',
            },
          ],
        },
      ],
    },
  },
  {
    resourceType: 'ValueSet',
    url: 'https://www.seenhealth.org/fhir/ValueSet/bedroom-hazards',
    version: '1',
    name: 'BedroomHazards',
    title: 'Bedroom hazards',
    status: 'active',
    meta: {
      tag: [
        {
          system: 'https://www.seenhealth.org/fhir/managed-by',
          code: 'code',
        },
      ],
    },
    compose: {
      include: [
        {
          system: ASSESSMENT_ANSWERS_SYSTEM,
          concept: [
            {
              code: 'bed-height',
              display: 'Bed height (e.g., too high or low)',
            },
            {
              code: 'blankets',
              display: 'Blankets hanging off bed',
            },
            {
              code: 'loose-rugs',
              display: 'Loose rugs',
            },
            {
              code: 'hard-clothes',
              display: 'Hard to reach clothes',
            },
            {
              code: 'other-specify',
              display: 'Other, please specify:',
            },
          ],
        },
      ],
    },
  },
  {
    resourceType: 'ValueSet',
    url: 'https://www.seenhealth.org/fhir/ValueSet/kitchen-hazards',
    version: '1',
    name: 'KitchenHazards',
    title: 'Kitchen hazards',
    status: 'active',
    meta: {
      tag: [
        {
          system: 'https://www.seenhealth.org/fhir/managed-by',
          code: 'code',
        },
      ],
    },
    compose: {
      include: [
        {
          system: ASSESSMENT_ANSWERS_SYSTEM,
          concept: [
            {
              code: 'dangerous-stove',
              display: 'Dangerous stove',
            },
            {
              code: 'kitchen-infestation',
              display: 'Infestation by rats or bugs',
            },
            {
              code: 'lack-of-warm-water',
              display: 'Lack of warm water',
            },
            {
              code: 'other-specify',
              display: 'Other, please specify:',
            },
          ],
        },
      ],
    },
  },
  {
    resourceType: 'ValueSet',
    url: 'https://www.seenhealth.org/fhir/ValueSet/cold-storage',
    version: '1',
    name: 'ColdStorage',
    title: 'Cold food storage',
    status: 'active',
    meta: {
      tag: [
        {
          system: 'https://www.seenhealth.org/fhir/managed-by',
          code: 'code',
        },
      ],
    },
    compose: {
      include: [
        {
          system: ASSESSMENT_ANSWERS_SYSTEM,
          concept: [
            {
              code: 'no-refrigerator',
              display: 'Does not have a working refrigerator on site',
            },
            {
              code: 'poor-temperature',
              display: 'Poor temperature control',
            },
            {
              code: 'food-space',
              display: 'Insufficient space for food storage',
            },
            {
              code: 'expired-food',
              display: 'Foul smelling/ expired foods',
            },
            {
              code: 'food-insecurity',
              display:
                'Does not have sufficient supply of foods (food insecurity)',
            },
            {
              code: 'other-specify',
              display: 'Other, please specify:',
            },
          ],
        },
      ],
    },
  },
  {
    resourceType: 'ValueSet',
    url: 'https://www.seenhealth.org/fhir/ValueSet/heating-hazards',
    version: '1',
    name: 'HeatingHazards',
    title: 'Heating and cooling hazards',
    status: 'active',
    meta: {
      tag: [
        {
          system: 'https://www.seenhealth.org/fhir/managed-by',
          code: 'code',
        },
      ],
    },
    compose: {
      include: [
        {
          system: ASSESSMENT_ANSWERS_SYSTEM,
          concept: [
            {
              code: 'too-hot',
              display: 'Too hot in summer',
            },
            {
              code: 'too-cold',
              display: 'Too cold in winter',
            },
            {
              code: 'heating-equipment',
              display: 'Potential dangers with heating equipment',
            },
            {
              code: 'other-specify',
              display: 'Other, please specify:',
            },
          ],
        },
      ],
    },
  },
  {
    resourceType: 'ValueSet',
    url: 'https://www.seenhealth.org/fhir/ValueSet/personal-safety',
    version: '1',
    name: 'PersonalSafety',
    title: 'Personal safety issues',
    status: 'active',
    meta: {
      tag: [
        {
          system: 'https://www.seenhealth.org/fhir/managed-by',
          code: 'code',
        },
      ],
    },
    compose: {
      include: [
        {
          system: ASSESSMENT_ANSWERS_SYSTEM,
          concept: [
            {
              code: 'fear-of-violence',
              display: 'Fear of violence',
            },
            {
              code: 'mailbox',
              display: 'Going to the mailbox',
            },
            {
              code: 'neighbors',
              display: 'Visiting neighbors',
            },
            {
              code: 'street-traffic',
              display: 'Heavy street traffic',
            },
            {
              code: 'weapons',
              display: 'Weapons in home',
            },
            {
              code: 'no-deadbolt',
              display: 'No deadbolt locks',
            },
            {
              code: 'other-specify',
              display: 'Other, please specify:',
            },
          ],
        },
      ],
    },
  },
  {
    resourceType: 'ValueSet',
    url: 'https://www.seenhealth.org/fhir/ValueSet/home-access',
    version: '1',
    name: 'HomeAccess',
    title: 'Home access issues',
    status: 'active',
    meta: {
      tag: [
        {
          system: 'https://www.seenhealth.org/fhir/managed-by',
          code: 'code',
        },
      ],
    },
    compose: {
      include: [
        {
          system: ASSESSMENT_ANSWERS_SYSTEM,
          concept: [
            {
              code: 'entry',
              display: 'Difficulty entering/leaving home',
            },
            {
              code: 'egress',
              display: 'Inadequate egress',
            },
            {
              code: 'walkways',
              display: 'Walkways not cleared/need repair',
            },
            {
              code: 'other-specify',
              display: 'Other, please specify:',
            },
          ],
        },
      ],
    },
  },
  {
    resourceType: 'ValueSet',
    url: 'https://www.seenhealth.org/fhir/ValueSet/pet-issues',
    version: '1',
    name: 'PetIssues',
    title: 'Pet issues',
    status: 'active',
    meta: {
      tag: [
        {
          system: 'https://www.seenhealth.org/fhir/managed-by',
          code: 'code',
        },
      ],
    },
    compose: {
      include: [
        {
          system: ASSESSMENT_ANSWERS_SYSTEM,
          concept: [
            {
              code: 'pet-care',
              display: 'Difficulty caring for pet(s)',
            },
            {
              code: 'pet-fall',
              display: 'Pet(s) pose fall risk',
            },
            {
              code: 'other-specify',
              display: 'Other, please specify:',
            },
          ],
        },
      ],
    },
  },
  {
    resourceType: 'ValueSet',
    url: 'https://www.seenhealth.org/fhir/ValueSet/infestations',
    version: '1',
    name: 'Infestations',
    title: 'Bug and rodent infestations',
    status: 'active',
    meta: {
      tag: [
        {
          system: 'https://www.seenhealth.org/fhir/managed-by',
          code: 'code',
        },
      ],
    },
    compose: {
      include: [
        {
          system: ASSESSMENT_ANSWERS_SYSTEM,
          concept: [
            {
              code: 'bed-bugs-now',
              display: 'Bed bugs currently',
            },
            {
              code: 'bed-bugs-history',
              display: 'Bed bugs history',
            },
            {
              code: 'rodents',
              display: 'Rodent infestation requiring treatment',
            },
            {
              code: 'other-specify',
              display: 'Other, please specify:',
            },
          ],
        },
      ],
    },
  },
  {
    resourceType: 'ValueSet',
    url: 'https://www.seenhealth.org/fhir/ValueSet/daily-decisions',
    version: '1',
    name: 'DailyDecisions',
    title: 'Daily decision making',
    status: 'active',
    meta: {
      tag: [
        {
          system: 'https://www.seenhealth.org/fhir/managed-by',
          code: 'code',
        },
      ],
    },
    compose: {
      include: [
        {
          system: ASSESSMENT_ANSWERS_SYSTEM,
          concept: [
            {
              code: 'decision-independent',
              display: 'Independent – decisions consistently reasonable',
            },
            {
              code: 'decision-modified',
              display:
                'Modified independence – some difficult in new situations only',
            },
            {
              code: 'decision-minimal',
              display:
                'Minimally impaired – in specific situations, decisions become poor or unsafe and cues/supervision necessary at those times',
            },
            {
              code: 'decision-moderate',
              display:
                'Moderately impaired – decisions consistently poor or unsafe and cues/supervision required at all times',
            },
            {
              code: 'decision-severe',
              display: 'Severely impaired – never/rarely made decisions',
            },
          ],
        },
      ],
    },
  },
  {
    resourceType: 'ValueSet',
    url: 'https://www.seenhealth.org/fhir/ValueSet/bladder-control-4-level',
    version: '1',
    name: 'BladderControl4Level',
    title: 'Bladder control',
    status: 'active',
    meta: {
      tag: [
        {
          system: 'https://www.seenhealth.org/fhir/managed-by',
          code: 'code',
        },
      ],
    },
    compose: {
      include: [
        {
          system: ASSESSMENT_ANSWERS_SYSTEM,
          concept: [
            {
              code: 'bladder-continent-no-device',
              display:
                'Continent – complete control; does not use any type of catheter or other urinary collection devices',
            },
            {
              code: 'bladder-occasional-not-daily',
              display:
                'Occasionally incontinent – incontinent episodes 2 or more times week, but not daily',
            },
            {
              code: 'bladder-incontinent-daily',
              display:
                'Incontinent – inadequate control, multiple daily episodes',
            },
            {
              code: 'bladder-device',
              display: 'Requires catheter or urinary device collection',
            },
          ],
        },
      ],
    },
  },
  {
    resourceType: 'ValueSet',
    url: 'https://www.seenhealth.org/fhir/ValueSet/bowel-control',
    version: '1',
    name: 'BowelControl',
    title: 'Bowel control',
    status: 'active',
    meta: {
      tag: [
        {
          system: 'https://www.seenhealth.org/fhir/managed-by',
          code: 'code',
        },
      ],
    },
    compose: {
      include: [
        {
          system: ASSESSMENT_ANSWERS_SYSTEM,
          concept: [
            {
              code: 'bowel-continent',
              display: 'Continent',
            },
            {
              code: 'bowel-occasional',
              display: 'Occasionally incontinent',
            },
            {
              code: 'bowel-incontinent',
              display: 'Incontinent',
            },
          ],
        },
      ],
    },
  },
  {
    resourceType: 'ValueSet',
    url: 'https://www.seenhealth.org/fhir/ValueSet/oral-med-management-itemized-setup',
    version: '1',
    name: 'OralMedManagementItemizedSetup',
    title: 'Oral medication management',
    status: 'active',
    meta: {
      tag: [
        {
          system: 'https://www.seenhealth.org/fhir/managed-by',
          code: 'code',
        },
      ],
    },
    compose: {
      include: [
        {
          system: ASSESSMENT_ANSWERS_SYSTEM,
          concept: [
            {
              code: 'oral-independent',
              display:
                'Independently and consistently takes correct oral medications at proper dosages at the correct times without any assistance or supervision',
            },
            {
              code: 'oral-setup-or-reminders',
              display:
                'Takes oral medications at correct times if (1) individual dosages are prepared in advance by another person OR (2) given daily reminders OR (3) someone develops a drug diary or chart',
            },
            {
              code: 'oral-med-independent-sometimes',
              display:
                'Takes oral medications independently some (but not all) of the time',
            },
            {
              code: 'oral-administered',
              display:
                'Does not take oral medications unless administered by someone else',
            },
            {
              code: 'oral-none',
              display: 'Not applicable - no orally prescribed medications',
            },
          ],
        },
      ],
    },
  },
  {
    resourceType: 'ValueSet',
    url: 'https://www.seenhealth.org/fhir/ValueSet/learning-barriers',
    version: '1',
    name: 'LearningBarriers',
    title: 'Learning barriers',
    status: 'active',
    meta: {
      tag: [
        {
          system: 'https://www.seenhealth.org/fhir/managed-by',
          code: 'code',
        },
      ],
    },
    compose: {
      include: [
        {
          system: ASSESSMENT_ANSWERS_SYSTEM,
          concept: [
            {
              code: 'barrier-visual',
              display: 'Visual',
            },
            {
              code: 'barrier-hearing',
              display: 'Hearing',
            },
            {
              code: 'barrier-read',
              display: 'Unable to read',
            },
            {
              code: 'barrier-comprehend',
              display: 'Unable to comprehend what is read',
            },
            {
              code: 'barrier-other',
              display: 'Other',
            },
            {
              code: 'barrier-none',
              display: 'None',
            },
          ],
        },
      ],
    },
  },
  {
    resourceType: 'ValueSet',
    url: 'https://www.seenhealth.org/fhir/ValueSet/education-recipients',
    version: '1',
    name: 'EducationRecipients',
    title: 'Education recipients',
    status: 'active',
    meta: {
      tag: [
        {
          system: 'https://www.seenhealth.org/fhir/managed-by',
          code: 'code',
        },
      ],
    },
    compose: {
      include: [
        {
          system: ASSESSMENT_ANSWERS_SYSTEM,
          concept: [
            {
              code: 'educated-participant',
              display: 'Participant',
            },
            {
              code: 'educated-family',
              display: 'Family member',
            },
            {
              code: 'educated-caregiver',
              display: 'Caregiver (if other than family)',
            },
          ],
        },
      ],
    },
  },
  {
    resourceType: 'ValueSet',
    url: 'https://www.seenhealth.org/fhir/ValueSet/learning-demonstration',
    version: '1',
    name: 'LearningDemonstration',
    title: 'Demonstration of learning',
    status: 'active',
    meta: {
      tag: [
        {
          system: 'https://www.seenhealth.org/fhir/managed-by',
          code: 'code',
        },
      ],
    },
    compose: {
      include: [
        {
          system: ASSESSMENT_ANSWERS_SYSTEM,
          concept: [
            {
              code: 'learn-participant-verbal',
              display: 'Participant verbalized understanding',
            },
            {
              code: 'learn-family-verbal',
              display: 'Family member verbalized understanding',
            },
            {
              code: 'learn-caregiver-verbal',
              display: 'Caregiver verbalized understanding',
            },
            {
              code: 'learn-participant-demo',
              display: 'Participant demonstrated correctly',
            },
            {
              code: 'learn-family-demo',
              display: 'Family member demonstrated correctly',
            },
            {
              code: 'learn-caregiver-demo',
              display: 'Caregiver demonstrated correctly',
            },
            {
              code: 'learn-unsuccessful',
              display: 'Demonstration unsuccessful',
            },
          ],
        },
      ],
    },
  },
  ...PCP_ANSWER_SETS,
  ...RD_ANSWER_SETS,
  ...ASSESSMENT_TEMPLATE_ANSWER_SETS,
  ...INSTRUMENT_ANSWER_SETS,
]
